import { AppType } from "client/Api/Apps/common";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration } from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useImperativeHandle,
	useState
} from "react";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	SaveGoogleAppModel,
	EditGoogleAppViewModel,
	saveGoogleApp
} from "client/Api/Apps/EditApp/saveApp";

const validation = createValidationConfiguration<SaveGoogleAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

export type EditGoogleAppProps = {
	initialSettings: EditAppSettings<EditGoogleAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditGoogleApp = forwardRef(
	(
		props: EditGoogleAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveGoogleAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveGoogleAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveGoogleAppModel>(
			formHandler,
			saveGoogleApp,
			AppType.Google
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
			</EditAppBase>
		);
	}
);

EditGoogleApp.displayName = "EditGoogleApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditGoogleAppViewModel>
): SaveGoogleAppModel {
	const {
		destinationWindow
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		destinationWindow
	};
}